import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from '../../../../ui/toast';
import {
  createSkill,
  deleteSkill,
  deleteSubSkill,
} from '../../../../utils/api_service/skill_api';
import {
  addNewSkill,
  removeSkill,
} from '../../../../utils/redux_store/features/skill_reducer';
import { removeSubSkill } from '../../../../utils/redux_store/features/subskill_reducer';
import { Skill, SubSkill } from '../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { ITEMS_PER_PAGE } from '../constants';
import { SortOption } from '../interfaces';

interface UseSkillsManagementParams {
  skills: SelectOptions[];
  subSkillList: SubSkill[];
}

export const useSkillsManagement = ({
  skills = [],
  subSkillList = [],
}: UseSkillsManagementParams) => {
  const dispatch = useDispatch();
  const [newSkillName, setNewSkillName] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [expandedSkills, setExpandedSkills] = useState<Set<string>>(new Set());
  const itemsPerPage = ITEMS_PER_PAGE;

  const getSubSkillsForSkill = (skillId: string): SubSkill[] => {
    return (subSkillList || []).filter(
      (subSkill) => subSkill.parentSkillId === skillId,
    );
  };

  const handleAddSkill = () => {
    if (!newSkillName.trim()) {
      toast.error('Please enter a skill name');
      return;
    }

    const skillLabel = newSkillName.trim();
    createSkill(skillLabel)
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
      })
      .then((response: Skill) => {
        dispatch(addNewSkill({ value: response.id, label: skillLabel }));
        setNewSkillName('');
        toast.success('Skill added successfully');
      })
      .catch(() => {
        toast.error('Failed to add skill');
      });
  };

  const handleDeleteSkill = (skillId: string) => {
    deleteSkill(skillId)
      .then((response) => {
        if (response.ok) {
          dispatch(removeSkill(skillId));
          toast.success('Skill deleted successfully');
        } else {
          toast.error('Failed to delete skill');
        }
      })
      .catch(() => {
        toast.error('Failed to delete skill');
      });
  };

  const handleDeleteSubSkill = (subSkillId: string) => {
    deleteSubSkill(subSkillId)
      .then((response) => {
        if (response.ok) {
          dispatch(removeSubSkill(subSkillId));
          toast.success('Sub-skill deleted successfully');
        } else {
          toast.error('Failed to delete sub-skill');
        }
      })
      .catch(() => {
        toast.error('Failed to delete sub-skill');
      });
  };

  const toggleSkillExpansion = (skillId: string) => {
    setExpandedSkills((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) {
        next.delete(skillId);
      } else {
        next.add(skillId);
      }
      return next;
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sortedAndFilteredSkills = useMemo(() => {
    let filtered = (skills || []).filter((skill) =>
      skill.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    filtered.sort((a, b) => {
      if (sortBy === 'name-asc') {
        return a.label.localeCompare(b.label);
      } else if (sortBy === 'name-desc') {
        return b.label.localeCompare(a.label);
      }
      return 0;
    });

    return filtered;
  }, [skills, searchQuery, sortBy]);

  const totalPages = Math.ceil(sortedAndFilteredSkills.length / itemsPerPage);
  const paginatedSkills = sortedAndFilteredSkills.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  return {
    newSkillName,
    setNewSkillName,
    searchQuery,
    setSearchQuery: handleSearchChange,
    currentPage,
    sortBy,
    setSortBy,
    expandedSkills,
    itemsPerPage,
    sortedAndFilteredSkills,
    totalPages,
    paginatedSkills,
    getSubSkillsForSkill,
    handleAddSkill,
    handleDeleteSkill,
    handleDeleteSubSkill,
    toggleSkillExpansion,
    handlePageChange,
  };
};
